import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { getJusfyAuthToken } from 'app/helpers/aiPlatformHandoff';

const RESERVED_PARAMS = ['embedded', 'token'];

export const buildEmbeddedUrl = (
  baseUrl: string,
  path: string,
  hostSearch = '',
  extraQuery = '',
): string => {
  const params = new URLSearchParams(hostSearch);
  params.set('embedded', 'true');
  const token = getJusfyAuthToken();
  if (token) params.set('token', token);
  else params.delete('token');
  new URLSearchParams(extraQuery).forEach((value, key) => {
    // `embedded` e `token` são do host: nem um parâmetro extra os sobrescreve.
    if (RESERVED_PARAMS.includes(key)) return;
    params.set(key, value);
  });
  return `${baseUrl.replace(/\/+$/, '')}${path}?${params.toString()}`;
};

/** Mesmos z-index do DriveFrame: o iframe expandido fica acima do resto da ficha. */
const EXPANDED_Z_INDEX = 40;

const IframeWrapper = styled.div<{ $height: string }>`
  position: relative;
  width: 100%;
  height: ${({ $height }) => $height};
  overflow: hidden;
`;

/*
  Um iframe não pinta fora da própria caixa: o drawer do app filho (`fixed
  inset-0`) cobre o viewport DO IFRAME. Com o overlay aberto o iframe vira
  `fixed`, com a janela inteira na vertical e da SUA borda esquerda até a direita
  da janela — `left` preservado, senão o conteúdo escorrega para debaixo da
  sidebar. O `IframeWrapper` segura o espaço na página, então nada abaixo salta.

  `width` explícito: iframe é elemento substituído e, com `width: auto`, cairia
  nos 300px intrínsecos em vez de esticar.
*/
const StyledIframe = styled.iframe<{ $expanded: boolean; $left: number }>`
  width: 100%;
  height: 100%;
  border: none;
  display: block;

  ${({ $expanded, $left }) =>
    $expanded &&
    `
      position: fixed;
      top: 0;
      left: ${$left}px;
      width: calc(100vw - ${$left}px);
      height: 100vh;
      z-index: ${EXPANDED_Z_INDEX};
    `}
`;

/*
  Escurece o que o iframe expandido não cobre: a faixa à esquerda (sidebar e
  margem). O tom vem do app filho, para os dois lados da borda pintarem o mesmo
  preto.
*/
const DimStrip = styled.div<{ $width: number; $dim: number }>`
  position: fixed;
  top: 0;
  bottom: 0;
  left: 0;
  width: ${({ $width }) => $width}px;
  z-index: ${EXPANDED_Z_INDEX - 1};
  background-color: ${({ $dim }) => `rgba(0, 0, 0, ${$dim})`};
`;

const LoadingOverlay = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #fff;
  z-index: 1;
`;

const DEFAULT_DIM = 0.8;

interface OverlayState {
  open: boolean;
  /** Borda esquerda do iframe no instante da abertura, preservada ao expandir. */
  left: number;
  dim: number;
}

const OVERLAY_CLOSED: OverlayState = { open: false, left: 0, dim: DEFAULT_DIM };

const getOrigin = (url: string): string => {
  try {
    return new URL(url).origin;
  } catch (_) {
    return '';
  }
};

/**
 * Protocolo de overlay do app filho: ele avisa quando abre um drawer/modal e o
 * host expande o iframe (ver `jusbank-front/src/lib/embedOverlay.ts`).
 */
export interface EmbeddedAppOverlayProtocol {
  /** Aviso do filho: `{ type, open, dim? }`. */
  stateMessage: string;
  /**
   * Resposta do host com a caixa que o conteúdo ocupava na página
   * (`{ type, contentBox }`, `contentBox: null` ao fechar). Sem ela o conteúdo
   * refluiria para a janela inteira e cobriria o que está em volta.
   */
  viewportMessage: string;
}

interface EmbeddedAppFrameProps {
  baseUrl: string;
  path: string;
  title: string;
  extraParams?: Record<string, string>;
  /** Altura do iframe; o padrão ocupa a viewport (páginas dedicadas). */
  height?: string;
  /**
   * Expande o iframe quando o app filho avisa que abriu um overlay. Só para
   * telas em que o iframe ocupa um pedaço da página; nas de 100vh não há o que
   * expandir.
   */
  overlay?: EmbeddedAppOverlayProtocol;
}

const EmbeddedAppFrame: React.FC<EmbeddedAppFrameProps> = ({
  baseUrl,
  path,
  title,
  extraParams,
  height = '100vh',
  overlay,
}) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [overlayState, setOverlayState] = useState<OverlayState>(OVERLAY_CLOSED);
  const [isLoading, setIsLoading] = useState(true);
  const { search } = useLocation();
  const extraQuery = new URLSearchParams(extraParams).toString();

  // O token só abre a sessão do app embutido. Recalcular o `src` a cada render
  // recarregaria o iframe no meio do uso quando o token mudasse.
  const src = useMemo(
    () => buildEmbeddedUrl(baseUrl, path, search, extraQuery),
    [baseUrl, path, search, extraQuery],
  );

  const handleLoad = useCallback(() => {
    setIsLoading(false);
  }, []);

  const stateMessage = overlay?.stateMessage;
  const viewportMessage = overlay?.viewportMessage;

  useEffect(() => {
    if (!stateMessage || !viewportMessage) return undefined;
    const origin = getOrigin(baseUrl);
    if (!origin) return undefined;

    // `null` solta o conteúdo; sem caixa o filho deixa de prender.
    const sendContentBox = (
      contentBox: { left: number; top: number; width: number; height: number } | null,
    ) => {
      iframeRef.current?.contentWindow?.postMessage(
        { type: viewportMessage, contentBox },
        origin,
      );
    };

    const handleMessage = (event: MessageEvent) => {
      // Vem de outro documento: só vale a origem do filho E o NOSSO iframe.
      if (event.origin !== origin) return;
      if (event.source !== iframeRef.current?.contentWindow) return;
      const data = event.data as { type?: string; open?: unknown; dim?: unknown } | null;
      if (data?.type !== stateMessage) return;

      if (data.open !== true) {
        sendContentBox(null);
        setOverlayState(OVERLAY_CLOSED);
        return;
      }
      const dim =
        typeof data.dim === 'number' && Number.isFinite(data.dim) && data.dim >= 0 && data.dim <= 1
          ? data.dim
          : DEFAULT_DIM;
      // Medido na moldura: ela não se mexe quando o iframe vira `fixed`.
      const rect = wrapperRef.current?.getBoundingClientRect();
      const left = rect?.left ?? 0;
      /*
        Em coordenadas do viewport do iframe expandido: ele preserva a borda
        esquerda da moldura (então `left` é 0) e começa no topo da janela (então
        `top` é onde a moldura estava). Mandado antes do `setOverlayState`, para
        a caixa chegar junto com a expansão e o conteúdo não pular.
      */
      if (rect) {
        sendContentBox({ left: 0, top: rect.top, width: rect.width, height: rect.height });
      }
      setOverlayState({ open: true, left, dim });
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [baseUrl, stateMessage, viewportMessage]);

  // Com overlay aberto a página não rola: o backdrop e o conteúdo dessincronizariam.
  useEffect(() => {
    if (!overlayState.open) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [overlayState.open]);

  return (
    <IframeWrapper ref={wrapperRef} $height={height}>
      {overlayState.open && (
        <DimStrip
          data-testid="embedded-dim-strip"
          $width={overlayState.left}
          $dim={overlayState.dim}
        />
      )}
      {isLoading && (
        <LoadingOverlay>
          <div className="spinner spinner-primary spinner-lg" />
        </LoadingOverlay>
      )}
      <StyledIframe
        ref={iframeRef}
        $expanded={overlayState.open}
        $left={overlayState.left}
        src={src}
        title={title}
        allow="clipboard-write"
        onLoad={handleLoad}
        style={{ marginTop: 0 }}
      />
    </IframeWrapper>
  );
};

export default EmbeddedAppFrame;
